import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useShallow } from "zustand/shallow";

import PageLoader from "components/PageLoader";
import PublicRoutes from "routes/PublicRoutes/PublicRoutes";
import { supabase } from "supabase/client";
import useBoundStore from "store";

interface AuthWrapperProps {
  children: React.ReactNode;
}

function AuthWrapper(props: AuthWrapperProps) {
  const { children } = props;
  const [initialLoading, setInitialLoading] = useState(true);
  const { auth, session, setUser } = useBoundStore(
    useShallow((state) => ({
      auth: state.user.auth,
      session: state.user.session,
      setUser: state.user.set,
    }))
  );

  async function setSession(): Promise<void> {
    const {
      data: { session: updatedSession },
    } = await supabase.auth.getSession();
    setInitialLoading(false);
    setUser({ session: updatedSession });
  }

  /**
   * Listen to auth state changes
   */
  useEffect(() => {
    setSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, updatedSession) => {
      setUser({ session: updatedSession });
      if (updatedSession) {
        Sentry.setUser({ id: updatedSession.user.app_metadata.id });
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [setUser]);

  /**
   * Set user data when session changes
   */
  useEffect(() => {
    if (session) {
      if (session.user && (session.user?.email !== auth?.email || !auth)) {
        setUser({
          auth: {
            id: session.user.app_metadata.id,
            email: session.user.email,
            profileCreated: session.user.user_metadata.profileCreated || false,
            provider: session.user.app_metadata.provider,
          },
        });
      }
    }
  }, [session, auth]);

  if (initialLoading) {
    return <PageLoader />;
  }

  if (session) {
    return !auth ? <PageLoader /> : children;
  }

  return <PublicRoutes />;
}

export default AuthWrapper;
